import { useCallback, useEffect, useRef, useState } from 'react';
import {
  COEXISTENCE_PHONE_NUMBER_ID_PLACEHOLDER,
  PostMessageEventSchema,
  WHATSAPP_SIGNUP_FAILURE_REASONS,
} from './types';
import type {
  ActiveWhatsAppSignupAttempt,
  FacebookLoginResponse,
  FBLoginOptions,
  PostMessageEvent,
  UseStoreWhatsAppIntegrationProps,
  UseStoreWhatsAppIntegrationReturn,
  UseWhatsAppSignupParams,
  WhatsAppSignupFailureReason,
  WhatsAppSignupMessageResolution,
  WhatsAppOnboardingConfiguration,
  WhatsAppSignupResult,
} from './types';
import { useCreateWhatsAppIntegration } from 'src/modules/shared/hooks/api/messaging/whatsapp/integrations';
import type { WhatsAppIntegrationCreateBodyParams as WhatsAppIntegrationCreateParams } from 'src/api/generated';
import { useAlert } from 'src/contexts/alert-context';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { observePopupClosed } from 'src/modules/shared/utils/popup-lifecycle.util';

const APP_ID = import.meta.env.VITE_META_APP_ID ?? '542019688162818';
const CONFIG_ID = import.meta.env.VITE_META_CONFIG_ID ?? '1087114077617460';
export const WHATSAPP_SIGNUP_TIMEOUT_MS = 15 * 60 * 1000;
const UNBOUND_META_DIALOG_QUARANTINE_KEY =
  'whatsapp-signup:unbound-meta-dialog-quarantine';

/**
 * Keeps an unbound timed-out Meta dialog quarantined across hook remounts and
 * page reloads. sessionStorage scopes the quarantine to the opener tab, which
 * is the same lifetime in which the surviving popup can keep messaging it.
 */
const hasUnboundMetaDialogQuarantine = (): boolean => {
  try {
    return (
      window.sessionStorage.getItem(UNBOUND_META_DIALOG_QUARANTINE_KEY) === '1'
    );
  } catch {
    return false;
  }
};

const quarantineUnboundMetaDialog = (): void => {
  try {
    window.sessionStorage.setItem(UNBOUND_META_DIALOG_QUARANTINE_KEY, '1');
  } catch {
    // A storage-restricted browser still retains the in-memory fallback below.
  }
};

const acceptMessageSourceForAttempt = (
  attempt: ActiveWhatsAppSignupAttempt,
  source: object | null,
  retiredSources: WeakSet<object>,
  quarantineUnboundMessages: boolean
): boolean => {
  if (
    quarantineUnboundMessages &&
    (!source || !attempt.messageSource || source !== attempt.messageSource)
  ) {
    return false;
  }
  if (source && retiredSources.has(source)) return false;
  if (source && attempt.messageSource && source !== attempt.messageSource) {
    return false;
  }
  if (source && !attempt.messageSource) attempt.messageSource = source;
  return true;
};

/**
 * Builds the Facebook Login configuration for WhatsApp Embedded Signup.
 *
 * @param configId - Environment-specific Embedded Signup configuration ID.
 * @returns Facebook Login options for the active configuration.
 */
export const buildWhatsAppLoginOptions = (
  configId: string,
  solutionId?: string
): FBLoginOptions => ({
  config_id: configId,
  response_type: 'code',
  override_default_response_type: true,
  extras: {
    setup: solutionId ? { solutionID: solutionId } : {},
    featureType: 'whatsapp_business_app_onboarding',
    sessionInfoVersion: '3',
  },
});

/**
 * Checks that a postMessage event originated from Facebook over HTTPS.
 *
 * @param origin - Browser event origin.
 * @returns True only for facebook.com or one of its subdomains.
 */
export const isTrustedFacebookOrigin = (origin: string): boolean => {
  try {
    const url = new URL(origin);
    const isFacebookHost =
      url.hostname === 'facebook.com' || url.hostname.endsWith('.facebook.com');
    return url.protocol === 'https:' && isFacebookHost;
  } catch {
    return false;
  }
};

/**
 * Parses the untrusted postMessage payload returned by Embedded Signup.
 *
 * @param eventData - Raw browser message payload.
 * @returns A validated Embedded Signup event, or null when invalid.
 */
export const parseWhatsAppPostMessageEvent = (
  eventData: unknown
): PostMessageEvent | null => {
  let parsedData: unknown = eventData;

  if (typeof eventData === 'string') {
    try {
      parsedData = JSON.parse(eventData);
    } catch {
      return null;
    }
  }

  const result = PostMessageEventSchema.safeParse(parsedData);
  return result.success ? result.data : null;
};

export const useInitFacebookSDK = () => {
  useEffect(() => {
    const loadFacebookSDK = () => {
      window.fbAsyncInit = function () {
        window.FB.init({
          appId: APP_ID,
          autoLogAppEvents: true,
          xfbml: true,
          version: 'v26.0',
        });
      };

      (function (d, s, id) {
        const fjs = d.getElementsByTagName(s)[0];
        if (d.getElementById(id)) return;
        const js = d.createElement(s) as HTMLScriptElement;
        js.id = id;
        js.src = 'https://connect.facebook.net/en_US/sdk.js';
        fjs.parentNode?.insertBefore(js, fjs);
      })(document, 'script', 'facebook-jssdk');
    };

    loadFacebookSDK();
  }, []);
};

/**
 * Normalizes one validated Meta event into an active-attempt action.
 *
 * @param data - Validated Embedded Signup provider event.
 * @returns A cancellation, safe failure, or supported session result.
 */
const resolveWhatsAppSignupMessage = (
  data: PostMessageEvent
): WhatsAppSignupMessageResolution => {
  if (data.event === 'CANCEL') return { action: 'cancel' };

  if (data.event === 'ERROR' || data.data.error_message) {
    return {
      action: 'failure',
      failureReason: WHATSAPP_SIGNUP_FAILURE_REASONS.META_ERROR,
    };
  }

  if (data.event === 'FINISH') {
    const { phone_number_id: phoneNumberId, waba_id: wabaId } = data.data;
    if (!phoneNumberId || !wabaId) {
      return { action: 'ignore' };
    }

    return {
      action: 'session',
      sessionInfo: {
        phone_number_id: phoneNumberId,
        waba_id: wabaId,
      },
    };
  }

  if (data.event === 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING') {
    const wabaId = data.data.waba_id;
    if (!wabaId) {
      return {
        action: 'failure',
        failureReason: WHATSAPP_SIGNUP_FAILURE_REASONS.INCOMPLETE_SESSION,
      };
    }

    return {
      action: 'session',
      sessionInfo: {
        phone_number_id: COEXISTENCE_PHONE_NUMBER_ID_PLACEHOLDER,
        waba_id: wabaId,
      },
    };
  }

  return {
    action: 'failure',
    failureReason: WHATSAPP_SIGNUP_FAILURE_REASONS.UNSUPPORTED_COMPLETION,
  };
};

/**
 * Launches WhatsApp Embedded Signup with the active Meta configuration.
 *
 * @param params - Failure callback for the launch attempt.
 * @returns Correlated result, launch state, and launch callback.
 */
export const useWhatsAppSignup = ({
  onSignupFailure,
}: UseWhatsAppSignupParams = {}) => {
  const [signupResult, setSignupResult] = useState<WhatsAppSignupResult | null>(
    null
  );
  const [signupInProgress, setSignupInProgress] = useState(false);
  const activeAttemptRef = useRef<ActiveWhatsAppSignupAttempt | null>(null);
  const retiredMessageSourcesRef = useRef(new WeakSet<object>());
  const quarantineUnboundMessagesRef = useRef(hasUnboundMetaDialogQuarantine());
  const nextAttemptIdRef = useRef(0);
  const attemptTimeoutRef = useRef<number | null>(null);
  const stopPopupObserverRef = useRef<(() => void) | null>(null);
  const mountedRef = useRef(true);
  const onSignupFailureRef = useRef(onSignupFailure);
  onSignupFailureRef.current = onSignupFailure;

  const clearAttemptWatchers = useCallback(() => {
    stopPopupObserverRef.current?.();
    stopPopupObserverRef.current = null;
    if (attemptTimeoutRef.current !== null) {
      window.clearTimeout(attemptTimeoutRef.current);
    }
    attemptTimeoutRef.current = null;
  }, []);

  const finishAttempt = useCallback(
    (attemptId: number) => {
      const attempt = activeAttemptRef.current;
      if (
        !mountedRef.current ||
        !attempt ||
        attempt.attemptId !== attemptId ||
        !attempt.loginResponse
      ) {
        return;
      }

      clearAttemptWatchers();
      activeAttemptRef.current = null;
      if (attempt.messageSource) {
        retiredMessageSourcesRef.current.add(attempt.messageSource);
      }
      setSignupInProgress(false);
      setSignupResult({
        attemptId,
        onboardingAttemptId: attempt.onboardingConfiguration.attemptId,
        billingMode: attempt.onboardingConfiguration.billingMode,
        loginResponse: attempt.loginResponse,
        sessionInfo: attempt.sessionInfo,
      });
    },
    [clearAttemptWatchers]
  );

  const terminateAttempt = useCallback(
    (
      attemptId: number,
      reason?: WhatsAppSignupFailureReason,
      quarantineUnboundMessages = false
    ) => {
      const attempt = activeAttemptRef.current;
      if (!attempt || attempt.attemptId !== attemptId) return;

      clearAttemptWatchers();
      activeAttemptRef.current = null;
      if (attempt.messageSource) {
        retiredMessageSourcesRef.current.add(attempt.messageSource);
      } else if (quarantineUnboundMessages) {
        // The SDK does not expose the popup Window. If an attempt expires
        // before its first message, a later event cannot be attributed safely
        // to that dialog or a retry. Code callbacks remain attempt-correlated,
        // so ignore all provider messages for the rest of this opener tab.
        // The session-scoped marker survives hook remounts and full reloads;
        // OAuth callbacks remain attempt-correlated and can complete retries.
        quarantineUnboundMessagesRef.current = true;
        quarantineUnboundMetaDialog();
      }
      if (!mountedRef.current) return;

      setSignupInProgress(false);
      if (reason) onSignupFailureRef.current?.(reason);
    },
    [clearAttemptWatchers]
  );

  const cancelWhatsAppSignup = useCallback(() => {
    const attempt = activeAttemptRef.current;
    if (!attempt) return;
    // Retire before closing: provider callbacks triggered by close must not
    // finalize this attempt or contaminate a retry. Backend finalization has
    // no active Meta attempt and is deliberately not cancellable here.
    terminateAttempt(
      attempt.attemptId,
      WHATSAPP_SIGNUP_FAILURE_REASONS.CANCELLED,
      true
    );
    try {
      attempt.popup?.close();
    } catch {
      // The local attempt is cancelled even if the browser prevents closure.
    }
  }, [terminateAttempt]);

  const handleMessageEvent = useCallback(
    (event: MessageEvent) => {
      if (!isTrustedFacebookOrigin(event.origin)) return;

      const data = parseWhatsAppPostMessageEvent(event.data);
      const attempt = activeAttemptRef.current;
      if (!data || !attempt) return;

      const messageSource =
        event.source && typeof event.source === 'object' ? event.source : null;

      const quarantineUnboundMessages =
        quarantineUnboundMessagesRef.current ||
        hasUnboundMetaDialogQuarantine();
      if (quarantineUnboundMessages) {
        quarantineUnboundMessagesRef.current = true;
      }
      if (
        !acceptMessageSourceForAttempt(
          attempt,
          messageSource,
          retiredMessageSourcesRef.current,
          quarantineUnboundMessages
        )
      ) {
        return;
      }

      const { attemptId } = attempt;
      const resolution = resolveWhatsAppSignupMessage(data);
      if (resolution.action === 'cancel') {
        terminateAttempt(attemptId, WHATSAPP_SIGNUP_FAILURE_REASONS.CANCELLED);
        return;
      }

      if (resolution.action === 'failure') {
        terminateAttempt(attemptId, resolution.failureReason);
        return;
      }

      if (resolution.action === 'ignore') return;

      if (attempt.sessionInfo || !resolution.sessionInfo) return;
      attempt.sessionInfo = resolution.sessionInfo;
      finishAttempt(attemptId);
    },
    [finishAttempt, terminateAttempt]
  );

  useEffect(() => {
    mountedRef.current = true;
    window.addEventListener('message', handleMessageEvent);
    return () => {
      mountedRef.current = false;
      clearAttemptWatchers();
      activeAttemptRef.current = null;
      window.removeEventListener('message', handleMessageEvent);
    };
  }, [clearAttemptWatchers, handleMessageEvent]);

  const launchWhatsAppSignup = useCallback(
    (onboardingConfiguration: WhatsAppOnboardingConfiguration) => {
      if (activeAttemptRef.current) return;

      const solutionId =
        onboardingConfiguration.billingMode === 'comos_managed'
          ? onboardingConfiguration.solutionId?.trim()
          : undefined;
      if (
        onboardingConfiguration.billingMode === 'comos_managed' &&
        !solutionId
      ) {
        onSignupFailureRef.current?.(
          WHATSAPP_SIGNUP_FAILURE_REASONS.CONFIGURATION_ERROR
        );
        return;
      }

      const attemptId = nextAttemptIdRef.current + 1;
      nextAttemptIdRef.current = attemptId;
      activeAttemptRef.current = {
        attemptId,
        onboardingConfiguration,
        loginResponse: null,
        sessionInfo: null,
        messageSource: null,
        popup: null,
      };
      setSignupResult(null);
      setSignupInProgress(true);
      attemptTimeoutRef.current = window.setTimeout(() => {
        terminateAttempt(
          attemptId,
          WHATSAPP_SIGNUP_FAILURE_REASONS.TIMEOUT,
          true
        );
      }, WHATSAPP_SIGNUP_TIMEOUT_MS);

      const fbLoginOptions = buildWhatsAppLoginOptions(CONFIG_ID, solutionId);
      const originalWindowOpen = window.open;
      const capturePopupOpen: typeof window.open = new Proxy<
        typeof window.open
      >(originalWindowOpen, {
        apply(
          target: typeof window.open,
          _thisArgument: unknown,
          argumentsList: unknown[]
        ): WindowProxy | null {
          const popup = target.apply(
            window,
            argumentsList as Parameters<typeof window.open>
          );
          const attempt = activeAttemptRef.current;
          if (popup && attempt?.attemptId === attemptId) {
            attempt.messageSource = popup;
            attempt.popup = popup;
            stopPopupObserverRef.current?.();
            stopPopupObserverRef.current = observePopupClosed(popup, () => {
              terminateAttempt(
                attemptId,
                WHATSAPP_SIGNUP_FAILURE_REASONS.CANCELLED
              );
            });
          }
          return popup;
        },
      });
      try {
        // FB.login opens its dialog synchronously. A proxy preserves the
        // browser's native popup behavior while exposing the exact
        // WindowProxy for retry event correlation.
        window.open = capturePopupOpen;
        window.FB.login((response: FacebookLoginResponse) => {
          const attempt = activeAttemptRef.current;
          if (
            !mountedRef.current ||
            !attempt ||
            attempt.attemptId !== attemptId
          ) {
            return;
          }

          if (!response.authResponse) {
            terminateAttempt(
              attemptId,
              WHATSAPP_SIGNUP_FAILURE_REASONS.CANCELLED
            );
            return;
          }

          attempt.loginResponse = response;
          finishAttempt(attemptId);
        }, fbLoginOptions);
      } catch {
        terminateAttempt(attemptId, WHATSAPP_SIGNUP_FAILURE_REASONS.META_ERROR);
      } finally {
        if (window.open === capturePopupOpen) {
          window.open = originalWindowOpen;
        }
      }
    },
    [finishAttempt, terminateAttempt]
  );

  return {
    signupInProgress,
    signupResult,
    launchWhatsAppSignup,
    cancelWhatsAppSignup,
  };
};

/**
 * Hook to store the WhatsApp integration in the database.
 */
export const useStoreWhatsAppIntegration = ({
  signupResult,
  onSuccess: onSuccessOverride,
  onError: onErrorOverride,
}: UseStoreWhatsAppIntegrationProps): UseStoreWhatsAppIntegrationReturn => {
  const { t } = useI18n();
  const submittedAttemptIdRef = useRef<number | null>(null);
  const { showAlert } = useAlert();
  const defaultOnError = () => {
    showAlert(t('settings.integrations.whatsapp.errors.finalization'), 'error');
  };
  const defaultOnSuccess = () => {
    showAlert(
      t('settings.integrations.whatsapp.connection.created'),
      'success'
    );
  };

  const { loading, error, integration, createWhatsAppIntegration } =
    useCreateWhatsAppIntegration(
      onSuccessOverride ?? defaultOnSuccess,
      onErrorOverride ?? defaultOnError
    );

  useEffect(() => {
    if (
      !signupResult ||
      submittedAttemptIdRef.current === signupResult.attemptId
    ) {
      return;
    }

    const code = signupResult.loginResponse.authResponse?.code;
    if (!code) return;

    submittedAttemptIdRef.current = signupResult.attemptId;
    const newWhatsAppIntegration: WhatsAppIntegrationCreateParams = {
      code: code,
      onboardingAttemptId: signupResult.onboardingAttemptId,
      ...(signupResult.sessionInfo
        ? {
            phoneNumberId: signupResult.sessionInfo.phone_number_id,
            wabaId: signupResult.sessionInfo.waba_id,
          }
        : {}),
    };
    createWhatsAppIntegration(newWhatsAppIntegration);
  }, [createWhatsAppIntegration, signupResult]);

  return {
    loading,
    error,
    integration: integration ?? null,
  };
};
